/**
 * The village's solids and the heroine's jump/dash body (ported from the wuxia sample's physics.ts + game.ts `step`;
 * our own simple model, research docs/research/WUXIA_WORLD.md §2; tuning `VILLAGE_PHYS`, data/starter.ts).
 * Every solid has a footprint (a turned box, a circle) and a height range; a top can be flat (box, circle), a
 * hip/pyramid roof (`roof`: eave at y1, rising `rise` to a ridge `rx` long, concave profile `roofCurve`) or an arch
 * (`arch`: the bridge deck, y1 at the ends, + rise at the middle).
 * - Standing: the highest standable top under the feet that is at most STEP above them (else the ground, 0).
 * - Blocking: a solid stops a horizontal move when its range overlaps the body (y0 < feet + BODY_H) and its top is more
 *   than STEP over the feet. Solids already overlapping at the old position are ignored (never stuck).
 * - Ceiling: a rising head stops at a solid's underside (y0).
 * Pure: no clock, no randomness; the caller steps it with dt (≤ 1/120 s) and applies the returned events' looks.
 */
import { VILLAGE_PHYS as PHYS } from '../data/starter';

/** Gravity up and the launch speed from the apex height and time (Pittman, GDC 2016). */
export const G_UP = (2 * PHYS.jumpH) / (PHYS.jumpT * PHYS.jumpT);
export const V0 = (2 * PHYS.jumpH) / PHYS.jumpT;

export interface Solid {
  kind: 'box' | 'circle' | 'roof' | 'arch';
  x: number;
  z: number;
  /** Turn about the vertical (radians; local x → world (cos, −sin)). */
  turn: number;
  hx: number;
  hz: number;
  r: number;
  /** Underside and top (roof: eave; arch: end height). */
  y0: number;
  y1: number;
  /** Roof: ridge over eave; arch: middle over ends. */
  rise: number;
  /** Roof: the ridge's half length (0: a pyramid). */
  rx: number;
  /** Can be stood on. */
  stand: boolean;
}

/** The roof profile: height fraction at t (0 eave … 1 ridge); the client's kit draws the same curve. */
export const roofCurve = (t: number): number => Math.pow(t, 1.6);

export function solid(o: Partial<Solid> & Pick<Solid, 'kind' | 'x' | 'z'>): Solid {
  return { turn: 0, hx: 0, hz: 0, r: 0, y0: 0, y1: 0, rise: 0, rx: 0, stand: true, ...o };
}

function local(s: Solid, x: number, z: number): [number, number] {
  const dx = x - s.x;
  const dz = z - s.z;
  const c = Math.cos(s.turn);
  const n = Math.sin(s.turn);
  return [dx * c - dz * n, dx * n + dz * c];
}

/** Is (x, z) over the footprint grown by `pad`? */
export function inside(s: Solid, x: number, z: number, pad = 0): boolean {
  if (s.kind === 'circle') return (x - s.x) ** 2 + (z - s.z) ** 2 <= (s.r + pad) ** 2;
  const [lx, lz] = local(s, x, z);
  return Math.abs(lx) <= s.hx + pad && Math.abs(lz) <= s.hz + pad;
}

/** The top at (x, z) (clamped onto the footprint). */
export function topAt(s: Solid, x: number, z: number): number {
  if (s.kind === 'box' || s.kind === 'circle') return s.y1;
  const [lx0, lz0] = local(s, x, z);
  const lx = Math.min(s.hx, Math.abs(lx0));
  const lz = Math.min(s.hz, Math.abs(lz0));
  if (s.kind === 'arch') return s.y1 + s.rise * (1 - (lx / s.hx) ** 2);
  const tx = s.hx > s.rx ? (s.hx - lx) / (s.hx - s.rx) : 1;
  const tz = (s.hz - lz) / s.hz;
  return s.y1 + s.rise * roofCurve(Math.max(0, Math.min(1, tx, tz)));
}

/** The village's collision: its solids and its water. */
export class VillageWorld {
  readonly solids: Solid[] = [];
  /** Water: (x, z) → true where the stream or pond is (no standing on the ground there). */
  water: (x: number, z: number) => boolean = () => false;

  /** The highest standable top under (x, z) no more than STEP above `py` (0: the ground). */
  standTop(x: number, z: number, py: number): number {
    let best = 0;
    for (const s of this.solids) {
      if (!s.stand || !inside(s, x, z)) continue;
      const t = topAt(s, x, z);
      if (t <= py + PHYS.step && t > best) best = t;
    }
    return best;
  }

  private blocks(s: Solid, x: number, z: number, py: number): boolean {
    if (!inside(s, x, z, PHYS.bodyR)) return false;
    return s.y0 < py + PHYS.bodyH && topAt(s, x, z) > py + PHYS.step;
  }

  /** Can the body move from (ox, oz) to (x, z) at feet height py? */
  free(ox: number, oz: number, x: number, z: number, py: number): boolean {
    for (const s of this.solids) if (this.blocks(s, x, z, py) && !this.blocks(s, ox, oz, py)) return false;
    return true;
  }

  /** The lowest underside between the head's old and new heights over (x, z), or null. */
  ceiling(x: number, z: number, headOld: number, headNew: number): number | null {
    let best: number | null = null;
    for (const s of this.solids) {
      if (s.y0 <= 0.01 || !inside(s, x, z, PHYS.bodyR * 0.5)) continue;
      if (s.y0 >= headOld - 0.02 && s.y0 < headNew && (best === null || s.y0 < best)) best = s.y0;
    }
    return best;
  }
}

/** The body's state (feet at (x, y, z)). */
export interface Body {
  x: number;
  z: number;
  y: number;
  vy: number;
  grounded: boolean;
  coyote: number;
  buffer: number;
  /** The jump was cut (released early). */
  cut: boolean;
  /** A dash was used in this airtime. */
  airDash: boolean;
  dashT: number;
  dashCd: number;
  dashV: number;
  dashX: number;
  dashZ: number;
  /** Knockback velocity (eases out: ×e^(−10 dt)). */
  knockX: number;
  knockZ: number;
  /** The last safe ground (back here after a step into the water). */
  safe: { x: number; z: number; y: number };
}

export function makeBody(x: number, z: number, y = 0): Body {
  return {
    x,
    z,
    y,
    vy: 0,
    grounded: true,
    coyote: 0,
    buffer: 0,
    cut: false,
    airDash: false,
    dashT: 0,
    dashCd: 0,
    dashV: PHYS.dashV,
    dashX: 0,
    dashZ: 1,
    knockX: 0,
    knockZ: 0,
    safe: { x, z, y },
  };
}

/** The soft edge circle the body cannot leave. */
export interface BodyEdge {
  readonly x: number;
  readonly z: number;
  readonly r: number;
}

/** One step's input: the wanted walk velocity (m/s), whether jump is held, the launch multiplier (riding hops lower). */
export interface BodyInput {
  vx: number;
  vz: number;
  jumpHeld: boolean;
  hop?: number;
}

/** What happened in a step (the caller plays dust, squash, counters). */
export interface BodyStepOut {
  jumped: boolean;
  /** Landed: the impact (0..1), else -1. */
  landed: number;
  /** Stopped or slid by a solid / the water's edge. */
  blocked: boolean;
  /** Fell into the water: back on the last safe spot. */
  splashed: boolean;
  /** Where the splash was (x, feet y, z), when `splashed`. */
  splashX: number;
  splashY: number;
  splashZ: number;
}

/** Releases a held jump: a rising jump is cut (short hop). */
export function bodyRelease(b: Body): void {
  if (!b.grounded && b.vy > 0 && !b.cut) {
    b.vy *= PHYS.cut;
    b.cut = true;
  }
}

/** Presses jump (buffered for `PHYS.buffer` s). */
export function bodyJumpPress(b: Body): void {
  b.buffer = PHYS.buffer;
}

/** Can the body dash now (off cooldown; on the ground, or the air dash unused)? */
export const bodyCanDash = (b: Body): boolean => b.dashCd <= 0 && (b.grounded || !b.airDash);

/** Starts a dash along the unit (dx, dz) at `speed` m/s for `time` s; `cd` sets the cooldown (a skill's dash keeps it). */
export function bodyDash(b: Body, dx: number, dz: number, speed: number = PHYS.dashV, time: number = PHYS.dashT, cd?: number): void {
  b.dashX = dx;
  b.dashZ = dz;
  b.dashT = time;
  b.dashV = speed;
  if (cd !== undefined) b.dashCd = cd;
  if (!b.grounded) b.airDash = true;
  b.vy = 0;
}

/** Knocks the body `dist` m along (dx, dz): a velocity whose eased-out integral (∫ v·e^(−10t)) is `dist`. */
export function bodyKnock(b: Body, dx: number, dz: number, dist: number): void {
  const l = Math.hypot(dx, dz) || 1;
  b.knockX = (dx / l) * dist * 10;
  b.knockZ = (dz / l) * dist * 10;
}

/** Puts the body at (x, z) on the top there, at rest. */
export function bodyTeleport(b: Body, world: VillageWorld, x: number, z: number): void {
  b.x = x;
  b.z = z;
  b.y = world.standTop(x, z, 0);
  b.vy = 0;
  b.grounded = true;
  b.knockX = b.knockZ = 0;
}

/** One body step (dt ≤ 1/120 s): jump, walk/dash with sliding, knockback, gravity, ceiling, landing, water. */
export function bodyStep(b: Body, world: VillageWorld, edge: BodyEdge, dt: number, input: BodyInput): BodyStepOut {
  const out: BodyStepOut = { jumped: false, landed: -1, blocked: false, splashed: false, splashX: 0, splashY: 0, splashZ: 0 };
  b.coyote = Math.max(0, b.coyote - dt);
  b.buffer = Math.max(0, b.buffer - dt);
  b.dashCd = Math.max(0, b.dashCd - dt);
  // Jump (buffered; on the ground or within coyote time).
  if (b.buffer > 0 && (b.grounded || b.coyote > 0) && b.dashT <= 0) {
    b.vy = V0 * (input.hop ?? 1);
    b.grounded = false;
    b.coyote = 0;
    b.buffer = 0;
    b.cut = false;
    out.jumped = true;
    if (!input.jumpHeld) bodyRelease(b);
  }
  // Horizontal velocity.
  let vx = b.dashT > 0 ? b.dashX * b.dashV : input.vx;
  let vz = b.dashT > 0 ? b.dashZ * b.dashV : input.vz;
  // Knockback (a blow taken), easing out.
  vx += b.knockX;
  vz += b.knockZ;
  const ease = Math.exp(-dt * 10);
  b.knockX *= ease;
  b.knockZ *= ease;
  // Move horizontally: full, else slide along x or z.
  const ox = b.x;
  const oz = b.z;
  let nx = ox + vx * dt;
  let nz = oz + vz * dt;
  const er = Math.hypot(nx - edge.x, nz - edge.z);
  if (er > edge.r) {
    nx = edge.x + ((nx - edge.x) * edge.r) / er;
    nz = edge.z + ((nz - edge.z) * edge.r) / er;
  }
  // Walking on the ground, the water's edge is a wall (only a jump or a dash goes in, and splashes).
  const py0 = b.y;
  const wet = (x: number, z: number): boolean => b.grounded && b.dashT <= 0 && py0 < 0.05 && world.water(x, z) && !world.water(ox, oz);
  const ok = (x: number, z: number): boolean => world.free(ox, oz, x, z, py0) && !wet(x, z);
  if (ok(nx, nz)) {
    b.x = nx;
    b.z = nz;
  } else if (ok(nx, oz)) {
    b.x = nx;
    out.blocked = true;
  } else if (ok(ox, nz)) {
    b.z = nz;
    out.blocked = true;
  } else out.blocked = true;
  // Vertical.
  if (b.dashT > 0) {
    b.dashT -= dt;
    b.vy = 0;
  } else if (!b.grounded) {
    b.vy -= (b.vy > 0 ? G_UP : G_UP * PHYS.fallK) * dt;
    b.vy = Math.max(b.vy, -PHYS.terminal);
  }
  const top = world.standTop(b.x, b.z, b.y);
  if (b.grounded) {
    if (top >= b.y - PHYS.snap) b.y = top;
    else {
      b.grounded = false;
      b.coyote = PHYS.coyote;
      b.vy = Math.min(b.vy, 0);
    }
  } else {
    let ny = b.y + b.vy * dt;
    if (b.vy > 0) {
      const c = world.ceiling(b.x, b.z, b.y + PHYS.bodyH, ny + PHYS.bodyH);
      if (c !== null) {
        ny = c - PHYS.bodyH - 0.01;
        b.vy = 0;
      }
    }
    if (ny <= top && b.vy <= 0) {
      // Landing.
      out.landed = Math.min(1, -b.vy / 14);
      b.y = top;
      b.vy = 0;
      b.grounded = true;
      b.airDash = false;
    } else b.y = ny;
  }
  if (b.grounded) {
    if (b.y < 0.05 && world.water(b.x, b.z)) {
      // In the water: back to the last safe spot.
      out.splashed = true;
      out.splashX = b.x;
      out.splashY = b.y;
      out.splashZ = b.z;
      b.x = b.safe.x;
      b.z = b.safe.z;
      b.y = b.safe.y;
    } else if (!world.water(b.x, b.z) || b.y > 0.05) {
      b.safe.x = b.x;
      b.safe.z = b.z;
      b.safe.y = b.y;
    }
  }
  return out;
}
